// Cache dos dados públicos no navegador (localStorage): a visita seguinte já abre com o
// que foi visto e atualiza em segundo plano. Entram só consultas desta lista, iguais para
// qualquer visitante, com dados válidos (bem-sucedidas, ou cuja atualização falhou apenas
// por falta de conexão). Nunca entram ['me', ...], ['admin', ...] nem nada que dependa da
// conta: votos, apoios e rascunhos podem revelar opinião política.
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { hashKey, hydrate, type DehydratedState, type QueryClient, type QueryKey } from '@tanstack/react-query'
import {
  removeOldestQuery,
  type PersistedClient,
  type PersistQueryClientProviderProps,
} from '@tanstack/react-query-persist-client'
import { toAppError } from './errors'
import { qk } from './query-keys'

/** Identificador do build (vite.config.ts): cada deploy descarta o cache do anterior. */
declare const __BUILD_ID__: string

export const PERSIST_STORAGE_KEY = 'poderaberto:dados-publicos'

/** Validade do que foi salvo. Ao abrir, o que já estiver velho é relido do Supabase. */
export const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000

/** Consultas com chave exatamente igual a uma destas podem ser salvas. */
const PUBLIC_KEYS = [qk.categories, qk.settings, qk.platformStats, qk.authSettings, qk.proposals.list]
const PUBLIC_KEY_HASHES = new Set(PUBLIC_KEYS.map((key) => hashKey(key)))

/** qk.proposals.detail(slug) sem o slug. */
const PROPOSAL_DETAIL = qk.proposals.detail('').slice(0, 2)

/** Propostas em revisão ou rejeitadas só aparecem para o autor e a moderação (RLS). */
const PUBLIC_PROPOSAL_STATUSES: ReadonlySet<unknown> = new Set(['published', 'archived'])

type PersistCandidate = {
  queryKey: QueryKey
  state: { status: string; data?: unknown; error?: unknown }
}

function isProposalDetailKey(queryKey: QueryKey): boolean {
  return (
    queryKey.length === 3 &&
    queryKey[0] === PROPOSAL_DETAIL[0] &&
    queryKey[1] === PROPOSAL_DETAIL[1] &&
    typeof queryKey[2] === 'string'
  )
}

export function isPersistedKey(queryKey: QueryKey): boolean {
  return PUBLIC_KEY_HASHES.has(hashKey(queryKey)) || isProposalDetailKey(queryKey)
}

function hasPublicStatus(proposal: unknown): boolean {
  return (
    typeof proposal === 'object' &&
    proposal !== null &&
    PUBLIC_PROPOSAL_STATUSES.has((proposal as { status?: unknown }).status)
  )
}

/**
 * Atualização que falhou só por falta de conexão (app aberto sem internet): os dados
 * anteriores continuam valendo. Outros erros (ex.: proposta removida, `not_found`) não.
 */
function isOfflineFailure(state: PersistCandidate['state']): boolean {
  return state.status === 'error' && state.data !== undefined && toAppError(state.error).code === 'network'
}

/** A consulta pode ir para o localStorage: com dados válidos, da lista e só com propostas públicas. */
export function shouldPersistQuery({ queryKey, state }: PersistCandidate): boolean {
  if (!(state.status === 'success' || isOfflineFailure(state)) || !isPersistedKey(queryKey)) return false
  if (queryKey[0] !== qk.proposals.all[0]) return true
  return Array.isArray(state.data) ? state.data.every(hasPublicStatus) : hasPublicStatus(state.data)
}

/** Grava como a última versão boa a consulta cuja atualização falhou sem conexão. */
function withLastGoodData(client: PersistedClient): PersistedClient {
  const queries = client.clientState.queries.map((query) =>
    query.state.status === 'error'
      ? {
          ...query,
          state: { ...query.state, status: 'success' as const, error: null, fetchFailureCount: 0, fetchFailureReason: null },
        }
      : query,
  )
  return { ...client, clientState: { ...client.clientState, queries } }
}

/**
 * Lê o cache salvo e devolve só o que ainda vale: mesmo build, gravado há menos de 24 h,
 * consultas da lista com dados de menos de 24 h. Cache de outro build, vencido ou
 * corrompido é apagado.
 */
export function readPersistedState(
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
  buster: string,
  now: number = Date.now(),
): DehydratedState | undefined {
  let raw: string | null
  try {
    raw = storage.getItem(PERSIST_STORAGE_KEY)
  } catch {
    return undefined
  }
  if (!raw) return undefined
  try {
    const persisted = JSON.parse(raw) as Partial<PersistedClient> | null
    const queries = persisted?.clientState?.queries
    if (
      persisted?.buster === buster &&
      typeof persisted.timestamp === 'number' &&
      now - persisted.timestamp <= PERSIST_MAX_AGE &&
      Array.isArray(queries)
    ) {
      return {
        mutations: [],
        queries: queries.filter((query) => shouldPersistQuery(query) && now - query.state.dataUpdatedAt <= PERSIST_MAX_AGE),
      }
    }
  } catch {
    // Conteúdo corrompido: apagado abaixo.
  }
  try {
    storage.removeItem(PERSIST_STORAGE_KEY)
  } catch {
    // Armazenamento indisponível: não há o que apagar.
  }
  return undefined
}

/** localStorage, se utilizável (navegação privada, bloqueio de cookies e cota zerada lançam erro). */
function availableStorage(): Storage | undefined {
  try {
    const storage = window.localStorage
    const probe = `${PERSIST_STORAGE_KEY}:teste`
    storage.setItem(probe, '1')
    storage.removeItem(probe)
    return storage
  } catch {
    return undefined
  }
}

/**
 * Prepara o cache salvo e devolve as opções do PersistQueryClientProvider.
 *
 * A leitura do localStorage é síncrona e acontece aqui, antes da primeira renderização:
 * as telas já nascem com os dados salvos, sem piscar "carregando" nem estado vazio. Por
 * isso o persister entregue ao provedor não lê de novo (restoreClient vazio); ele só grava
 * as mudanças, no máximo uma vez por segundo.
 */
export function setupQueryPersistence(queryClient: QueryClient): PersistQueryClientProviderProps['persistOptions'] {
  // Consulta salva precisa durar na memória pelo menos o mesmo que no armazenamento:
  // com o gcTime padrão (5 min), ela sumiria do cache salvo pouco depois de sair da tela.
  for (const key of [...PUBLIC_KEYS, PROPOSAL_DETAIL]) {
    queryClient.setQueryDefaults(key, { gcTime: PERSIST_MAX_AGE })
  }

  const storage = availableStorage()
  const restored = storage ? readPersistedState(storage, __BUILD_ID__) : undefined
  if (restored) hydrate(queryClient, restored)

  const persister = createSyncStoragePersister({
    storage,
    key: PERSIST_STORAGE_KEY,
    throttleTime: 1000,
    // Cota cheia: descarta as consultas mais antigas até caber.
    retry: removeOldestQuery,
  })

  return {
    persister: {
      ...persister,
      persistClient: (client) => persister.persistClient(withLastGoodData(client)),
      restoreClient: () => undefined,
    },
    maxAge: PERSIST_MAX_AGE,
    buster: __BUILD_ID__,
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQuery,
      // Mutações (votos, comentários) nunca vão para o armazenamento.
      shouldDehydrateMutation: () => false,
    },
  }
}
